import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  BarChart3, ChevronDown, ChevronUp, Eye, QrCode, MousePointerClick,
  Users, Inbox, CalendarCheck, TrendingUp, Loader2,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

/**
 * CardAnalytics — compact, expandable performance panel shown directly
 * beneath the talent's Digital Business Card. Compact view is a clean
 * four-metric summary; "View Analytics" expands the full private dashboard
 * (range filters, trend chart, traffic sources, conversion). Every talent
 * sees ONLY their own numbers — the API is scoped to the signed-in user.
 */

const RANGES = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

const SOURCE_META = {
  direct: { label: 'Direct', color: '#64748b' },
  card_qr: { label: 'QR Code', color: '#06b6d4' },
  short_link: { label: 'Short Link', color: '#0ea5e9' },
  linkedin: { label: 'LinkedIn', color: '#0a66c2' },
  facebook: { label: 'Facebook', color: '#1877f2' },
  instagram: { label: 'Instagram', color: '#d6249f' },
  tiktok: { label: 'TikTok', color: '#111827' },
  x: { label: 'X', color: '#111827' },
  email: { label: 'Email', color: '#f59e0b' },
  message: { label: 'Message', color: '#22c55e' },
  copy_link: { label: 'Copied Link', color: '#8b5cf6' },
};

const fmtBucket = (iso, granularity) => {
  try {
    const d = new Date(iso);
    if (granularity === 'hour') return d.toLocaleTimeString([], { hour: 'numeric' });
    if (granularity === 'month') return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const StatTile = ({ icon: Icon, label, value, accent, testid, suffix, tint }) => (
  <div className={`rounded-xl border p-3 transition-colors ${tint || 'border-gray-100 bg-gray-50/60'}`} data-testid={testid}>
    <div className="flex items-center gap-1.5 text-xs text-gray-600">
      <Icon className={`w-3.5 h-3.5 ${accent}`} />
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-1 text-xl font-bold text-gray-900 tabular-nums">
      {value}{suffix || ''}
    </div>
  </div>
);

const CardAnalytics = () => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/talent/card/stats').then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const loadAnalytics = useCallback(async (rk) => {
    setLoading(true);
    try {
      const r = await api.get('/talent/card/analytics', { params: { range: rk } });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadAnalytics(range);
  }, [open, range, loadAnalytics]);

  if (!summary) return null;

  const s = summary;
  const full = data?.summary || s;
  const series = (data?.timeseries || []).map((t) => ({
    ...t,
    label: fmtBucket(t.bucket, data?.granularity),
  }));
  const sources = data?.sources || [];
  const maxSrc = Math.max(1, ...sources.map((x) => x.views + x.scans));

  return (
    <Card className="border-cyan-200 shadow-sm bg-gradient-to-br from-cyan-50 via-teal-50 to-sky-50" data-testid="card-analytics">
      <CardContent className="p-5 sm:p-6">
        {/* Compact header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-white/70 border border-cyan-200 grid place-items-center shadow-sm">
              <BarChart3 className="w-4 h-4 text-cyan-600" />
            </span>
            <h3 className="text-sm font-bold tracking-wide text-teal-900 uppercase" data-testid="card-analytics-title">
              Card Performance
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            className="text-cyan-700 hover:text-cyan-800 hover:bg-cyan-100/60 -mr-2"
            data-testid="card-analytics-toggle"
          >
            {open ? 'Hide' : 'View Analytics'}
            {open ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
        </div>

        {/* Compact summary (all-time) — complementary tinted metric boxes */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4">
          <StatTile icon={Eye} accent="text-cyan-600" label="Profile Views" value={s.profile_views ?? 0} testid="summary-views" tint="border-cyan-200 bg-cyan-100/50" />
          <StatTile icon={QrCode} accent="text-sky-600" label="QR Scans" value={s.qr_scans ?? 0} testid="summary-scans" tint="border-sky-200 bg-sky-100/50" />
          <StatTile icon={MousePointerClick} accent="text-violet-600" label="Link Clicks" value={s.link_clicks ?? 0} testid="summary-clicks" tint="border-violet-200 bg-violet-100/50" />
          <StatTile icon={Inbox} accent="text-emerald-600" label="Booking Requests" value={s.booking_requests ?? 0} testid="summary-requests" tint="border-emerald-200 bg-emerald-100/50" />
        </div>

        {/* Expanded dashboard */}
        {open && (
          <div className="mt-6 pt-5 border-t border-cyan-200/70" data-testid="card-analytics-expanded">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h4 className="text-base font-bold text-gray-900">Digital Business Card Analytics</h4>
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" data-testid="analytics-range-tabs">
                {RANGES.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => setRange(r.key)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      range === r.key ? 'bg-white text-cyan-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                    }`}
                    data-testid={`analytics-range-${r.key}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="py-12 flex justify-center" data-testid="analytics-loading">
                <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
              </div>
            ) : (
              <>
                {/* Full metric grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4">
                  <StatTile icon={Eye} accent="text-cyan-500" label="Profile Views" value={full.profile_views ?? 0} testid="metric-views" />
                  <StatTile icon={Users} accent="text-indigo-500" label="Unique Visitors" value={full.unique_visitors ?? 0} testid="metric-uniques" />
                  <StatTile icon={QrCode} accent="text-sky-500" label="QR Scans" value={full.qr_scans ?? 0} testid="metric-scans" />
                  <StatTile icon={MousePointerClick} accent="text-violet-500" label="Link Clicks" value={full.link_clicks ?? 0} testid="metric-clicks" />
                  <StatTile icon={Inbox} accent="text-emerald-500" label="Booking Requests" value={full.booking_requests ?? 0} testid="metric-requests" />
                  <StatTile icon={CalendarCheck} accent="text-green-600" label="Confirmed" value={full.confirmed_bookings ?? 0} testid="metric-confirmed" />
                  <StatTile icon={TrendingUp} accent="text-amber-500" label="Conversion" value={full.conversion_rate ?? 0} suffix="%" testid="metric-conversion" />
                </div>

                {/* Trend chart */}
                <div className="mt-6" data-testid="analytics-chart">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Views &amp; engagement over time</p>
                  {series.length === 0 ? (
                    <div className="h-[220px] flex items-center justify-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl" data-testid="analytics-chart-empty">
                      No activity recorded in this period yet.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={series} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gScans" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={16} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={34} />
                        <Tooltip
                          contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                          labelStyle={{ fontWeight: 600, color: '#0f172a' }}
                        />
                        <Area type="monotone" dataKey="views" name="Views" stroke="#06b6d4" strokeWidth={2} fill="url(#gViews)" />
                        <Area type="monotone" dataKey="scans" name="QR Scans" stroke="#10b981" strokeWidth={2} fill="url(#gScans)" />
                        <Area type="monotone" dataKey="requests" name="Requests" stroke="#f59e0b" strokeWidth={2} fillOpacity={0} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Traffic sources */}
                <div className="mt-6" data-testid="analytics-sources">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Traffic sources</p>
                  {sources.length === 0 ? (
                    <p className="text-sm text-gray-400" data-testid="analytics-sources-empty">No tracked sources yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {sources.map((row) => {
                        const meta = SOURCE_META[row.source] || { label: row.source, color: '#64748b' };
                        const total = row.views + row.scans;
                        const pct = Math.round((total / maxSrc) * 100);
                        return (
                          <div key={row.source} className="flex items-center gap-3" data-testid={`source-${row.source}`}>
                            <span className="w-24 shrink-0 text-xs font-medium text-gray-700 truncate">{meta.label}</span>
                            <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                            </div>
                            <span className="w-16 shrink-0 text-right text-xs text-gray-500 tabular-nums">
                              {total} <span className="text-gray-300">·</span> {row.unique_visitors}u
                            </span>
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-gray-400 pt-1">Totals shown as interactions · unique visitors (u). Bot traffic excluded.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CardAnalytics;
